import _Store from '../../../store/store'
import { findTag } from '../helpers'
/**
 * @typedef {import('../js-doc').V1CreateInvoiceRequest} V1CreateInvoiceRequest
 * @typedef {import('../js-doc').V1UpdateInvoiceRequest} V1UpdateInvoiceRequest
 * @typedef {import('../js-doc').V1PurchaseInvoiceRequest} V1PurchaseInvoiceRequest
 * @typedef {import('../js-doc').V1RejectInvoiceRequest} V1RejectInvoiceRequest
 * @typedef {import('../js-doc').V1VerifyInvoiceRequest} V1VerifyInvoiceRequest
 * @typedef {import('../js-doc').V1WaitOnVerificationInvoiceRequest} V1WaitOnVerificationInvoiceRequest
 * @typedef {import('../js-doc').V1BypassVerificationInvoiceRequest} V1BypassVerificationInvoiceRequest
 * @typedef {import('../js-doc').V1RevertInvoiceRequest} V1RevertInvoiceRequest
 * @typedef {import('../js-doc').V1RegenerateInvoiceDocumentRequest} V1RegenerateInvoiceDocumentRequest
 * @typedef {import('../js-doc').V2CreateInvoiceRequest} V2CreateInvoiceRequest
 * @typedef {import('../js-doc').V2UpdateInvoiceRequest} V2UpdateInvoiceRequest
 * @typedef {import('../js-doc').V2InvoiceResponse} V2InvoiceResponse
 * @typedef {import('../js-doc').V2CreateInvoiceDocumentRequest} V2CreateInvoiceDocumentRequest
 * @typedef {import('../js-doc').V2UpdateInvoiceDocumentsRequest} V2UpdateInvoiceDocumentsRequest
 * @typedef {import('../js-doc').V2PurchaseInvoiceRequest} V2PurchaseInvoiceRequest
 * @typedef {import('../js-doc').V2RejectInvoiceRequest} V2RejectInvoiceRequest
 * @typedef {import('../js-doc').V2VerifyInvoiceRequest} V2VerifyInvoiceRequest
 * @typedef {import('../js-doc').V2RevertInvoiceRequest} V2RevertInvoiceRequest
 * @typedef {import('../js-doc').V2RegenerateInvoiceDocumentRequest} V2RegenerateInvoiceDocumentRequest
 * @typedef {import('../js-doc').V2MappingOptions} V2MappingOptions
 */

/**
 * Uses V1 document response body to create the V2 documents request body.
 *
 * If not present, it uses the V1 request body to create the V2 documents request body.
 *
 * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request body
 * @param {*=} v1Response - V1 create invoice response body
 * @returns {V2CreateInvoiceDocumentRequest[]} V2 create invoice request body
 */
const mapCreateInvoiceDocumentV1RequestToV2Request = (v1Request, v1Response = undefined) => {
  if (v1Response) {
    return (v1Response.invoice_documents).map((document) => {
      return {
        id: document.id,
        externalUrl: document.filestack_url,
        name: document.name,
        type: 'uploaded',
        label: 'other',
        internalUrl: document.url,
      }
    })
  }

  return (v1Request.invoice_documents || []).map((document) => {
    return {
      externalUrl: document.filestack_url,
      name: document.name,
      type: 'uploaded',
      label: 'other',
      internalUrl: document.url,
    }
  })
}

/**
 * Maps the V1 invoice update type to the V2 invoice status
 *
 * @param {string} v1Status - V1 invoice update type
 * @returns {string} V2 invoice status
 */
export const mapInvoiceUpdateType = (v1Status) => {
  switch (v1Status) {
    case 'approved':
    case 'paid':
      return 'purchased'
    case 'declined':
      return 'rejected'
    case 'pending':
    default:
      return 'under_review'
  }
}

/**
 * Uses V1 request body to create the V2 request body.
 *
 * If the V1 response body is present, it appends the ID and Display ID
 * to the V2 request body.
 *
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request body
 * @param {*=} v1Response - V1 create invoice response body
 * @returns {V2CreateInvoiceRequest} - V2 create invoice request body
 */
export const mapCreateInvoiceV1RequestToV2Request = (v2MappingOptions, v1Request, v1Response) => {
  if (v1Request.is_buyout == null) {
    v1Request.is_buyout = false
  }
  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    id: v1Response?.id,
    advance: v1Request.advance,
    brokerId: v1Request.debtor_id === _Store.debtorNotFound.id
      ? null
      : v1Request.debtor_id,
    clientId: v1Request.client_id,
    detention: v1Request.detention,
    buyoutId: v1Request.buyout_id,
    displayId: String(v1Response?.display_id),
    documents: mapCreateInvoiceDocumentV1RequestToV2Request(v1Request, v1Response),
    expedited: v1Request.transfer_type === 'wire'
      ? true
      : false,
    lineHaulRate: v1Request.primary_rate,
    loadNumber: v1Request.load_number,
    lumper: v1Request.lumper,
    memo: v1Request.memo,
    note: v1Request.note,
  }
}

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1PurchaseInvoiceRequest} v1Request - V1 purchase request body
 * @returns {V2PurchaseInvoiceRequest} - V2 purchase request body
 */
export const mapPurchaseInvoiceV1RequestToV2Request = (v2MappingOptions, v1Request) => {
  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    deduction: v1Request.approved_invoice_adjustment_amount,
  }
}

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1RejectInvoiceRequest} v1Request - V1 reject request body
 * @returns {V2RejectInvoiceRequest} - V2 reject request body
 */
export const mapRejectInvoiceV1RequestToV2Request = (v2MappingOptions, v1Request) => {
  const key = findTag(v1Request.declined_reason.toLowerCase())
  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    key: key,
    note: v1Request.notes,
  }
}

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1VerifyInvoiceRequest} v1Request - V1 verify request body
 * @returns {V2VerifyInvoiceRequest} - V2 verify request body
 */
export const mapVerifyInvoiceV1RequestToV2Request = (v2MappingOptions, v1Request) => {
  const contactType = v1Request.talked_to_contact_method?.toLowerCase() === 'email'
    ? 'email'
    : 'phone'
  let notes = v1Request.notes
  if (!notes) {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    notes = `Done by ${user.email || 'Anonnymous'}`
  }

  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    contactPerson: v1Request.talked_to || 'Default contact person',
    contactType: contactType,
    status: 'verified',
    notes: notes,
  }
}

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1WaitOnVerificationInvoiceRequest} v1Request - V1 verify request body
 * @returns {V2VerifyInvoiceRequest} - V2 verify request body
 */
export const mapWaitOnVerificationInvoiceV1RequestToV2Request = (v2MappingOptions, v1Request) => {
  const contactType = v1Request.talked_to_contact_method?.toLowerCase() === 'email'
    ? 'email'
    : 'phone'
  let notes = v1Request.notes
  if (!notes) {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    notes = `Done by ${user.email || 'Anonnymous'}`
  }

  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    contactPerson: v1Request.talked_to || 'Default contact person',
    contactType: contactType,
    status: 'in_progress',
    notes: notes,
  }
}

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1BypassVerificationInvoiceRequest} v1Request - V1 verify request body
 * @returns {V2VerifyInvoiceRequest} - V2 verify request body
 */
export const mapBypassVerificationInvoiceV1RequestToV2Request = (v2MappingOptions, v1Request) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const notes = `Done by ${user.email || 'Anonnymous'}`

  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    contactPerson: 'Default contact person',
    contactType: 'phone',
    status: 'bypassed',
    notes: notes,
  }
}

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1RevertInvoiceRequest} v1Request - V1 revert request body
 * @returns {V2RevertInvoiceRequest} - V2 revert request body
 */
export const mapRevertInvoiceV1RequestToV2Request = (v2MappingOptions, v1Request) => {
  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
  }
}

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1RegenerateInvoiceDocumentRequest} v1Request - V1 regenerate invoice document request body
 * @returns {V2RegenerateInvoiceDocumentRequest} - V2 regenerate invoice document request body
 */
// eslint-disable-next-line no-unused-vars
export const mapRegenerateInvoiceDocumentV1RequestToV2Request = (v2MappingOptions, v1Request) => {
  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
  }
}

/**
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1UpdateInvoiceRequest} v1Request - V1 update invoice request body
 * @param {*=} v1Response - V1 update invoice response
 * @returns {V2UpdateInvoiceRequest} - V2 update invoice request body
 */
export const mapUpdateInvoiceV1RequestToV2Request = (v2MappingOptions, v1Request, v1Response) => {
  const addingDocs = (v1Response?.invoice_documents || v1Request.documents.adding || []).filter((addingDoc) => {
    return v1Request.documents.adding.some((responseDoc) => {
      return responseDoc.url === addingDoc.url
    })
  })

  /*** @type {V2CreateInvoiceDocumentRequest[]} */
  const v2NewDocs = addingDocs.map((document) => {
    // Format the documents to API spec
    return {
      id: document.id,
      name: document.name,
      internalUrl: document.url,
      externalUrl: document.filestack_url,
      label: 'other',
      type: 'uploaded',
    }
  })
  /*** @type {V2UpdateInvoiceDocumentsRequest} */
  const documents = {
    toAdd: v2NewDocs,
    toDelete: v1Request.documents.removing,
  }
  return {
    // V2 debtor not found has an ID of _Store.debtorNotFound.id
    brokerId: v1Request.debtor_id === _Store.debtorNotFound.id
      ? null
      : v1Request.debtor_id,
    documents,
    lineHaulRate: v1Request.primary_rate,
    lumper: v1Request.lumper,
    advance: v1Request.advance,
    detention: v1Request.detention,
    memo: v1Request.memo,
    note: v1Request.note,
    clientId: v1Request.client_id,
    loadNumber: v1Request.load_number,
    expedited: v1Request.transfer_type === 'wire'
      ? true
      : false,
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
  }
}
